import { getTenantTokenWithoutStore } from '../../clients/shell/session';
import {
  AuthContextEnum,
  IAuthTokenService
} from '../../services/authTokenService';

import { getServices } from '../../infra/startup';
import {
  LoginInputType,
  SetServiceDependencies
} from '../../infra/startup/types';
import { UserContextEnum } from '../../interface/types';
import { ISessionService } from '../../services/session';
import userContextEnumToAuthContextEnum from '../authTokenService/utils/userContextEnumToAuthContextEnum';
import ITenantHandlerService from '../tenantHandler/ITenantHandlerService';
import IAuthProviderService from './IAuthProviderService';
import { AuthProviderServiceParams } from './types';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import { isTokenExpiredOrExpiringSoon } from '../../utils/tokenUtils/isTokenExpiredOrExpiringSoon';
import { container } from 'tsyringe';
import { InternalService } from '../../core/services';

// Minutes before expiration to consider a token as expiring soon
const MINUTES_BEFORE_EXPIRATION_TO_REFRESH_TOKEN = 1;

export default class AuthProviderService
  extends InternalService
  implements IAuthProviderService
{
  private _tenantHandlerService: ITenantHandlerService;
  private _sessionService: ISessionService;
  private _authTokenService: IAuthTokenService;
  private _authContext: AuthContextEnum;
  private _tenantId: string;
  private _refreshPromise: Promise<boolean> | null = null;
  private _minutesBeforeExpiredTokenToRefresh =
    MINUTES_BEFORE_EXPIRATION_TO_REFRESH_TOKEN;

  constructor(options?: AuthProviderServiceParams) {
    super();
    const { authContext, tenantId } = options || {};
    this._authContext = authContext;
    this._tenantId = tenantId;
  }

  public setDependencies({ services }: SetServiceDependencies): void {
    const { tenantHandlerService, sessionService, authTokenService } = services;
    this._tenantHandlerService = tenantHandlerService;
    this._sessionService = sessionService;
    this._authTokenService = authTokenService;

    const loginProps = container.resolve<LoginInputType>('LoginProps');
    this._minutesBeforeExpiredTokenToRefresh =
      loginProps?.minutesBeforeExpiredTokenToRefresh ||
      this._minutesBeforeExpiredTokenToRefresh;

    this._markAsReady();
  }

  // TODO: Temporary to unblock
  public createOrgedAuthProvider(tenantId: string = ''): IAuthProviderService {
    const result = new AuthProviderService({
      authContext: AuthContextEnum.tenant,
      tenantId
    });
    result.setDependencies({
      services: getServices(),
      clients: undefined,
      repositories: undefined
    });
    return result;
  }

  // TODO: Temporary to unblock
  public createOrglessAuthProvider(): IAuthProviderService {
    const result = new AuthProviderService({
      authContext: AuthContextEnum.tenantless
    });
    result.setDependencies({
      services: getServices(),
      clients: undefined,
      repositories: undefined
    });
    return result;
  }

  // TODO: Temporary to unblock. Need to be removed and use a AuthContextEnum instead
  public createAuthProviderByUserContextEnum(
    userContext: UserContextEnum
  ): IAuthProviderService {
    const authContext = userContextEnumToAuthContextEnum(userContext);

    const result = new AuthProviderService({
      authContext
    });
    result.setDependencies({
      services: getServices(),
      clients: undefined,
      repositories: undefined
    });
    return result;
  }

  public createAuthProviderByAuthContextEnum(
    authContext: AuthContextEnum
  ): IAuthProviderService {
    const result = new AuthProviderService({
      authContext
    });
    result.setDependencies({
      services: getServices(),
      clients: undefined,
      repositories: undefined
    });
    return result;
  }

  private async _getProperToken(): Promise<string> {
    let token;
    if (this._tenantId) {
      token = await this._getTokenByTenantId();
    } else {
      token = this._authTokenService.getToken(this._authContext)?.token;
    }
    return token;
  }
  /**
   * Gets an access token, automatically refreshing if needed
   * @param forceRefresh Force a refresh regardless of token expiration state
   * @returns A valid access token
   */
  public async getAccessToken(forceRefresh?: boolean): Promise<string> {
    let token = await this._getProperToken();

    // Check if token is missing, expired, or about to expire
    if (
      this._sessionService.isLoggedIn() &&
      (!token ||
        forceRefresh ||
        isTokenExpiredOrExpiringSoon(
          token,
          this._minutesBeforeExpiredTokenToRefresh
        ))
    ) {
      // Handle concurrent refresh requests using a semaphore pattern
      await this.waitTokenRefresh();
      // After waiting for the refresh, get the updated token
      token = await this._getProperToken();
    }

    internalLogger.debug.d3({
      className: 'AuthProviderService',
      methodName: 'getAccessToken',
      message: `Returning access token for context ${this._authContext}`,
      parameters: { forceRefresh },
      returnValue: { token }
    });
    return token;
  }

  public async waitTokenRefresh(): Promise<boolean> {
    // If there's already a refresh in progress, wait for it to complete
    if (this._refreshPromise) {
      await this._refreshPromise;
      // After waiting for the refresh, get the updated token
      return true;
    }

    // Start a new refresh and store the promise
    this._refreshPromise = this.forceRefresh();

    try {
      await this._refreshPromise;
      // After refresh, get the token again
      return true;
    } finally {
      // Clear the refresh promise when done
      this._refreshPromise = null;
    }
  }

  /**
   * Refreshes the token for the current session
   * @returns True if refresh was successful, false otherwise
   */
  public async forceRefresh(): Promise<boolean> {
    try {
      const tenantsIdMap = this._tenantHandlerService.getTenantIdsMap();
      /* istanbul ignore next */
      internalLogger?.debug?.d2({
        className: 'AuthProviderService',
        methodName: 'forceRefresh',
        message: 'Refreshing token for tenantsIdMap',
        parameters: { tenantsIdMap }
      });

      await this._sessionService.refresh({
        tenantsIdMap
      });
      /* istanbul ignore next */
      internalLogger?.debug?.d3({
        className: 'AuthProviderService',
        methodName: 'forceRefresh',
        message: 'Token refreshed successfully',
        returnValue: true
      });
      return true;
    } catch (error) {
      internalLogger?.error('Error refreshing token', error);

      // Only logout if we have a critical error that prevents token refresh
      if (
        this._sessionService.isLoggedIn() &&
        (error?.response?.status === 401 || error?.response?.status === 403)
      ) {
        internalLogger?.warn('Invalid session, logging out user');
        await this._sessionService.logout();
      }

      return false;
    }
  }

  private async _getTokenByTenantId(): Promise<string> {
    const targetUserContext =
      this._authContext === AuthContextEnum.tenant
        ? UserContextEnum.customer
        : UserContextEnum.organization;

    // TODO: Currently using a deprecated service. Should be upgraded to use the client.
    const { stratusToken } = await getTenantTokenWithoutStore(
      targetUserContext,
      this._tenantId,
      this._authTokenService?.getToken(this._authContext)?.token
    );
    return stratusToken;
  }
}
